import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { CONTACT, REFERENCES } from "../../content/catalog";
import Reveal from "./Reveal";

function Marquee({ text, alt = false }) {
  const unit = `${text} `;
  return (
    <div className={alt ? "marq alt" : "marq"} aria-hidden="true">
      <div>{unit}{unit}</div>
    </div>
  );
}

export function Marquees() {
  const { t } = useTranslation();
  return (
    <div className="bands">
      <Marquee text={t("marquee.services")} />
      <Marquee text={t("marquee.brands")} alt />
    </div>
  );
}

export function Services() {
  const { t } = useTranslation();
  const items = t("services.items", { returnObjects: true });
  const tilt = useRef(false);

  useEffect(() => {
    const fine = window.matchMedia("(hover: hover) and (pointer: fine)").matches;
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    tilt.current = fine && !reduced;
  }, []);

  const onMove = (event) => {
    if (!tilt.current) return;
    const el = event.currentTarget;
    const rect = el.getBoundingClientRect();
    const px = event.clientX - rect.left;
    const py = event.clientY - rect.top;
    el.style.setProperty("--x", `${px}px`);
    el.style.setProperty("--y", `${py}px`);
    const ry = (px / rect.width - 0.5) * 14;
    const rx = -(py / rect.height - 0.5) * 14;
    el.style.transform = `perspective(800px) rotateY(${ry}deg) rotateX(${rx}deg)`;
  };

  const onLeave = (event) => {
    const el = event.currentTarget;
    el.style.transform = "";
    el.style.removeProperty("--x");
    el.style.removeProperty("--y");
  };

  return (
    <section id="hizmetler">
      <div className="blob" style={{ background: "var(--accent2)", right: 0, top: 100 }} />
      <span className="lbl">{"// 01 — "}{t("services.kicker")}</span>
      <Reveal as="h2">
        {t("services.title1")}
        <br />
        <span className="accent">{t("services.title2")}</span>
      </Reveal>
      <div className="svc">
        {items.map((item, index) => (
          <Reveal
            key={item.title}
            className="card"
            index={index}
            onMouseMove={onMove}
            onMouseLeave={onLeave}
          >
            <span className="n">{String(index + 1).padStart(2, "0")}</span>
            <div className="ic" aria-hidden="true">{item.icon}</div>
            <h3>{item.title}</h3>
            <p>{item.body}</p>
            <div className="chips">
              {item.chips.map((chip) => <span key={chip}>{chip}</span>)}
            </div>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

export function References() {
  const { t } = useTranslation();
  return (
    <section id="referanslar">
      <span className="lbl">{"// 02 — "}{t("references.kicker")}</span>
      <Reveal as="h2">
        {t("references.title1")}
        <br />
        <span className="o light">{t("references.title2")}</span>
      </Reveal>
      <div className="refs">
        {REFERENCES.map((ref, index) => {
          const copy = t(`references.items.${ref.id}`, { returnObjects: true });
          return (
            <Reveal
              as="a"
              key={ref.id}
              index={index}
              className={`ref ${ref.tone}${ref.wide ? " w" : ""}`}
              href={ref.href}
              target="_blank"
              rel="noopener noreferrer"
            >
              <div className="top">
                <span>{copy.kicker}</span>
                <span>{ref.host}</span>
              </div>
              <span className="arrow" aria-hidden="true">↗</span>
              <div>
                <h3>{copy.name}</h3>
                <p>{copy.body}</p>
              </div>
              <span className="big" aria-hidden="true">{ref.symbol}</span>
            </Reveal>
          );
        })}
      </div>
    </section>
  );
}

export function Process() {
  const { t } = useTranslation();
  const steps = t("process.steps", { returnObjects: true });
  return (
    <section id="surec">
      <span className="lbl">{"// 03 — "}{t("process.kicker")}</span>
      <Reveal as="h2">
        {t("process.title1")}
        <br />
        {t("process.title2")}
      </Reveal>
      <div className="proc">
        {steps.map((step, index) => (
          <Reveal key={step.title} className="st" index={index}>
            <b>{String(index + 1).padStart(2, "0")}</b>
            <h3>{step.title}</h3>
            <p>{step.body}</p>
          </Reveal>
        ))}
      </div>
    </section>
  );
}

export function About() {
  const { t } = useTranslation();
  const storySteps = t("about.story.steps", { returnObjects: true });
  return (
    <section id="hakkimizda">
      <div className="about">
        <Reveal>
          <span className="lbl">{"// 04 — "}{t("about.kicker")}</span>
          <h2>
            {t("about.title1")}
            <br />
            {t("about.title2")}
          </h2>
          <p>{t("about.body")}</p>
        </Reveal>
        <Reveal className="box" index={1}>
          <div>{t("about.cmd")}</div>
          <div>{t("about.locationLabel")}: {t("about.location")}</div>
          <div>{t("about.scholarshipLabel")}: {t("about.scholarship")}</div>
          <div>{t("about.internLabel")}: {t("about.intern")}</div>
          <div>
            {t("about.statusLabel")}: <span className="ok">● {t("about.status")}</span>
            <span className="caret" aria-hidden="true">_</span>
          </div>
        </Reveal>
      </div>
      <Reveal className="robot-stage">
        <div className="rs-copy">
          <span className="lbl">{"// "}{t("about.robot.kicker")}</span>
          <h3>
            {t("about.robot.title1")}
            <br />
            <span className="accent">{t("about.robot.title2")}</span>
          </h3>
          <p>{t("about.robot.body")}</p>
        </div>
        <img
          className="rs-img"
          src="/robot.webp"
          alt={t("about.robot.alt")}
          width="1152"
          height="864"
          loading="lazy"
          decoding="async"
        />
      </Reveal>
      <div className="story">
        <Reveal className="founder">
          <span className="lbl">{"// "}{t("about.story.kicker")}</span>
          <h3>
            {t("about.story.title1")}
            <br />
            <span className="accent">{t("about.story.title2")}</span>
          </h3>
          <p>{t("about.story.intro")}</p>
          <div className="who">
            <span className="avatar" aria-hidden="true">ED</span>
            <div>
              <b>{t("about.story.name")}</b>
              <span>{t("about.story.role")} · KODPİLOT</span>
            </div>
          </div>
        </Reveal>
        <ol className="timeline">
          {storySteps.map((step, index) => (
            <Reveal as="li" key={step.title} index={index}>
              <b>{String(index + 1).padStart(2, "0")}</b>
              <h4>{step.title}</h4>
              <p>{step.body}</p>
            </Reveal>
          ))}
        </ol>
      </div>
    </section>
  );
}

export function Contact() {
  const { t } = useTranslation();
  const subject = encodeURIComponent(t("contact.subject"));
  const waText = encodeURIComponent(t("contact.whatsappText"));
  return (
    <section className="contact" id="iletisim">
      <div className="blob" style={{ background: "var(--accent)", left: "30%", top: "20%", opacity: 0.18 }} />
      <span className="lbl">{"// 05 — "}{t("contact.kicker")}</span>
      <Reveal as="h2" className="disp">
        {t("contact.title1")}
        <br />
        <span className="g">{t("contact.title2")}</span>
      </Reveal>
      <a className="mail" href={`mailto:${CONTACT.email}`}>{CONTACT.email}</a>
      <p className="contact-extra mono">
        <a href={CONTACT.phoneHref}>{CONTACT.phoneDisplay}</a>
        <a href={CONTACT.maps} target="_blank" rel="noopener noreferrer">{CONTACT.address}</a>
      </p>
      <div className="btns">
        <a className="btn p" href={`mailto:${CONTACT.email}?subject=${subject}`}>{t("contact.cta")} ↗</a>
        <a
          className="btn wa"
          href={`https://wa.me/${CONTACT.whatsappNumber}?text=${waText}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M17.47 14.38c-.3-.15-1.76-.87-2.03-.97-.27-.1-.47-.15-.67.15-.2.3-.77.97-.94 1.17-.17.2-.35.22-.65.07-.3-.15-1.26-.46-2.4-1.48-.89-.79-1.49-1.77-1.66-2.07-.17-.3-.02-.46.13-.61.13-.13.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.07-.15-.67-1.62-.92-2.22-.24-.58-.49-.5-.67-.51h-.57c-.2 0-.52.07-.8.37-.27.3-1.04 1.02-1.04 2.48s1.07 2.88 1.22 3.08c.15.2 2.1 3.2 5.08 4.49.71.31 1.26.49 1.69.63.71.23 1.36.2 1.87.12.57-.08 1.76-.72 2.01-1.41.25-.7.25-1.29.17-1.41-.07-.13-.27-.2-.57-.35zM12.04 21.5h-.01a9.45 9.45 0 0 1-4.82-1.32l-.35-.2-3.58.94.96-3.49-.22-.36a9.43 9.43 0 0 1-1.45-5.04c0-5.22 4.25-9.47 9.48-9.47 2.53 0 4.9.99 6.69 2.78a9.4 9.4 0 0 1 2.77 6.7c0 5.22-4.25 9.46-9.47 9.46zM20.52 3.45A11.38 11.38 0 0 0 12.04.1C5.74.1.62 5.22.62 11.52c0 2.01.53 3.98 1.53 5.71L.5 23.5l6.4-1.68a11.4 11.4 0 0 0 5.14 1.23h.01c6.3 0 11.43-5.12 11.43-11.43 0-3.05-1.19-5.92-3.36-8.17z" />
          </svg>
          {t("contact.whatsapp")}
        </a>
      </div>
      <Reveal as="figure" className="factory">
        <img
          src="/factory.webp"
          alt={t("contact.factory.alt")}
          width="1024"
          height="576"
          loading="lazy"
          decoding="async"
        />
        <figcaption>
          <span className="lbl">{"// "}{t("contact.factory.kicker")}</span>
          <b>{t("contact.factory.caption")}</b>
        </figcaption>
      </Reveal>
    </section>
  );
}
